// ═══════════════════════════════════════════════════════
// CLIENT LAYOUT
// Shell principal do portal do cliente: sidebar, header,
// seletor de período, notificações, skeleton de loading,
// suitability gate e roteamento de páginas via PAGES map.
// ═══════════════════════════════════════════════════════
import { useState, useEffect, useCallback, useRef, useMemo, Suspense, ReactNode } from "react";
import { useLocation, useNavigate, pathToPageId, pageIdToPath } from "../router/index.js";
import { ClientProvider, ClientContextValue } from "../ClientContext.jsx";
import {
  supabase,
  fetchSuitability,
  fetchLatestSnapshot,
  fetchSnapshotHistory,
  fetchSnapshotByPeriod,
  fetchNotifications,
  markNotificationsRead,
  fetchClientMeta,
  upsertClientMeta,
  fetchLatestPtax,
} from "../supabaseClient.js";
import { useQuery } from "../useQuery.js";
import { useIsMobile, usePageTitle } from "../shared/hooks.js";
import { snapToData, DATA_EMPTY } from "../shared/dataUtils.js";
import { safeText, setFormatterPrivacy } from "../shared/formatters.js";
import { logWarn, logError } from "../shared/logger.js";
import { flattenNav, NAV, PERFIL_NAV } from "../shared/navConfig.js";
import {
  SkeletonBox,
  SkeletonCard,
  SkeletonMetricRow,
  SkeletonPulse,
} from "../shared/Skeletons.jsx";
import Sidebar from "../components/Sidebar.jsx";
import ClientSuitabilityGate from "../components/ClientSuitabilityGate.jsx";
import OnboardingTour from "../components/OnboardingTour.jsx";
import OnboardingWizard from "../shared/OnboardingWizard.jsx";
import CommandPalette from "../components/CommandPalette.jsx";
import ShortcutsHelp from "../components/ShortcutsHelp.jsx";
import PageTransition from "../components/PageTransition.jsx";
import PageErrorBoundary from "./layout/PageErrorBoundary.jsx";
import LayoutHeader from "./layout/LayoutHeader.jsx";
import PeriodSelector from "./layout/PeriodSelector.jsx";
import NotificationsPanel from "./layout/NotificationsPanel.jsx";
import { useKeyboardShortcuts } from "../shared/useKeyboardShortcuts.js";
import { useWebVitals } from "../shared/useWebVitals.js";
import useEventTracker from "../shared/useEventTracker.js";
import MobileTabBar from "../components/MobileTabBar.jsx";
import PushNotificationBanner from "../components/PushNotificationBanner.js";
import { useSwipeNavigation } from "../shared/useSwipeNavigation.js";
import { LIGHT, DARK } from "../shared/theme.js";
import useSessionTimeout from "../shared/useSessionTimeout.js";
import SessionTimeoutWarning from "../components/SessionTimeoutWarning.jsx";

// ── Páginas importadas sob demanda —————————————————————
// Elas continuam em App.jsx para este ciclo de refactoring.
// ClientLayout recebe-as via prop `pages` (render map) para
// evitar dependência circular enquanto todas residem no monólito.
// Na Sprint seguinte cada Page será extraída para src/pages/*.
// ─────────────────────────────────────────────────────────

interface ClientLayoutProps {
  clientId: string;
  clientName?: string;
  clientNameProp?: string;
  profile: unknown;
  userEmail: string;
  onLogout: () => void;
  dark: boolean;
  setDark: (dark: boolean) => void;
  pages: (opts: { hasSnapshot: boolean }) => Record<string, ReactNode>;
}

export default function ClientLayout({
  clientId,
  clientName: clientNameProp,
  profile,
  userEmail,
  onLogout,
  dark,
  setDark,
  pages, // { dashboard, carteira, performance, ... } — JSX pré-computado
}: ClientLayoutProps) {
  const T = dark ? DARK : LIGHT;
  const isMobile = useIsMobile();

  // ── Web Vitals monitoring (Feature 2: Core Web Vitals) ─────────────────
  useWebVitals();

  // ── Event tracking (Task D: Expanded Audit Trail) ─────────────────
  const { trackEvent } = useEventTracker(clientId);

  // ── Routing: URL-based via History API ────────────────────
  const location = useLocation();
  const navigateFn = useNavigate();
  const page = pathToPageId(location.pathname);
  const setPage = useCallback((pageId: string) => navigateFn(pageIdToPath(pageId)), [navigateFn]);

  // ── Set document.title based on current page ────────────────────
  usePageTitle(page);

  // ── Track page views for audit trail ────────────────────
  useEffect(() => {
    if (page) {
      trackEvent("page_view", "navigation", { page_id: page }, `/${page}`);
    }
  }, [page, trackEvent]);

  // Redireciona "/" para "/dashboard" silenciosamente
  useEffect(() => {
    if (location.pathname === "/" || location.pathname === "") {
      navigateFn(pageIdToPath("dashboard"), { replace: true });
    }
  }, [location.pathname, navigateFn]);

  // -- Session timeout (60 minutes, warning at 55 minutes) --
  const {
    showWarning: showSessionWarning,
    remainingSeconds,
    dismissWarning,
  } = useSessionTimeout(60 * 60 * 1000, 5 * 60 * 1000);

  const handleSessionExpired = useCallback(() => {
    supabase.auth
      .signOut()
      .catch((e) => logError("ClientLayout", e as Error, { context: "session expired signout" }));
    navigateFn("/login", { replace: true });
  }, [navigateFn]);
  const mainRef = useRef<HTMLElement>(null);

  // ── Swipe navigation (Mobile & PWA — Fase 5) ──────────────────
  const swipeRef = useSwipeNavigation(page, setPage, isMobile) as any;
  const [col, setCol] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [snapshotData, setSnapshotData] = useState<any>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(true);
  const [snapHistory, setSnapHistory] = useState<any[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null); // null = latest
  const [suitStatus, setSuitStatus] = useState<string | null>(null); // null|"pendente"|"parcial"|"completo"
  const [showSuitGate, setShowSuitGate] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [clientSuitData, setClientSuitData] = useState<any>(null);
  const [showEditSuit, setShowEditSuit] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    // Show onboarding only if not completed
    return typeof window !== "undefined" && !localStorage.getItem("lpa_onboarding_done");
  });
  const [showAutoWizard, setShowAutoWizard] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [ptaxRate, setPtaxRate] = useState(5.8); // fallback conservador

  // ── Privacy mode — masks financial values with asterisks ────────
  const [privacyMode, setPrivacyMode] = useState(() => {
    const stored = typeof window !== "undefined" && localStorage.getItem("lpa_privacy_mode");
    const enabled = stored === "true";
    setFormatterPrivacy(enabled); // sync formatter flag on mount
    return enabled;
  });

  useEffect(() => {
    localStorage.setItem("lpa_privacy_mode", String(privacyMode));
    setFormatterPrivacy(privacyMode);
  }, [privacyMode]);

  // ── Keyboard shortcuts hook ─────────────────────────────────────
  useKeyboardShortcuts({
    setPage,
    onOpenCommandPalette: () => setShowCommandPalette(true),
    onOpenShortcutsHelp: () => setShowShortcutsHelp(true),
    onCloseOverlay: () => {
      setShowCommandPalette(false);
      setShowShortcutsHelp(false);
    },
    currentPage: page,
  });

  // ── Buscar taxa PTAX para conversão USD → BRL ─────────────────
  useEffect(() => {
    fetchLatestPtax(5.8)
      .then(setPtaxRate)
      .catch((e) =>
        logWarn("ClientLayout", "PTAX fetch failed, using fallback", { error: e?.message })
      );
  }, []);

  // ── useQuery: snapshot + suitability em paralelo ─────────────────
  const { data: _clientQueryData, loading: _clientQueryLoading } = useQuery(
    useCallback(
      () =>
        Promise.all([
          fetchLatestSnapshot(clientId),
          fetchSnapshotHistory(clientId),
          fetchSuitability(clientId).catch((e: any) => {
            logWarn("ClientLayout", "Suitability fetch failed", { error: e?.message });
            return null;
          }),
        ]).then(([snap, hist, suit]: any) => ({ snap, hist, suit })),
      [clientId]
    ),
    [clientId],
    { key: "client-snapshot", ttl: 60_000, skip: false, fallback: null }
  ) as any;

  useEffect(() => {
    if (_clientQueryData) {
      const { snap, hist, suit } = _clientQueryData;
      if (snap) setSnapshotData(snapToData(snap));
      setSnapHistory(hist || []);
      const status = suit?.status || "pendente";
      setSuitStatus(status);
      setClientSuitData(suit || null);
      // Ativar gate parcial se o onboarding está incompleto (pendente/parcial)
      // e o cliente já passou pelo fluxo inicial (está no portal).
      // NÃO acionar o gate full-screen automaticamente — usa banner.
    }
  }, [_clientQueryData]);

  useEffect(() => {
    setSnapshotLoading(_clientQueryLoading);
  }, [_clientQueryLoading]);

  // Auto-trigger OnboardingWizard on first login when suitability is pending
  useEffect(() => {
    if (
      suitStatus === "pendente" &&
      !snapshotLoading &&
      typeof window !== "undefined" &&
      !localStorage.getItem("lpa_wizard_auto_shown")
    ) {
      setShowAutoWizard(true);
      localStorage.setItem("lpa_wizard_auto_shown", "1");
    }
  }, [suitStatus, snapshotLoading]);

  // ── useQuery: notificações ────────────────────────────────────────
  const { data: _notifData } = useQuery(
    useCallback(
      () =>
        fetchNotifications(clientId).catch((e: any) => {
          logWarn("ClientLayout", "Notifications fetch failed", { error: e?.message });
          return [];
        }),
      [clientId]
    ),
    [clientId],
    { key: "client-notifications", ttl: 30_000, skip: false, fallback: [] }
  ) as any;
  useEffect(() => {
    if (_notifData) setNotifications(_notifData);
  }, [_notifData]);

  // ── useQuery: clients_meta ────────────────────────────────────────
  const { data: clientMetaRow } = useQuery(
    useCallback(
      () =>
        fetchClientMeta(clientId).catch((e: any) => {
          logWarn("ClientLayout", "ClientMeta fetch failed", { error: e?.message });
          return null;
        }),
      [clientId]
    ),
    [clientId],
    { key: "client-meta", ttl: 300_000, skip: false, fallback: null }
  ) as any;

  // ── T2: Notificação automática de próxima revisão ─────────────────
  useEffect(() => {
    if (!clientMetaRow?.next_review) return;
    const reviewDate = new Date(clientMetaRow.next_review);
    if (isNaN(reviewDate.getTime())) return;
    const diffDays = Math.ceil((reviewDate.getTime() - new Date().getTime()) / 86_400_000);
    if (diffDays > 30 || diffDays < 0) return;
    const id = `review_${clientId}_${clientMetaRow.next_review}`;
    setNotifications((prev) =>
      prev.some((n) => n.id === id)
        ? prev
        : [
            {
              id,
              type: "review",
              title: "Revisão de carteira próxima",
              message:
                diffDays === 0
                  ? "Sua revisão de carteira é hoje!"
                  : `Próxima revisão em ${diffDays} dia(s) — ${clientMetaRow.next_review}`,
              read: false,
              created_at: new Date().toISOString(),
            },
            ...prev,
          ]
    );
  }, [clientMetaRow, clientId]);

  useEffect(() => {
    if (_clientQueryData?.suit) setClientSuitData(_clientQueryData.suit);
  }, [_clientQueryData]);

  // ── Scroll to top on page change ────────────────────────────
  useEffect(() => {
    if (mainRef.current) (mainRef.current as HTMLElement).scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  // ── Supabase Realtime ─────────────────────────────────────────────
  useEffect(() => {
    const channel = supabase
      .channel(`lpa_snapshots_${clientId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "snapshots",
          filter: `client_id=eq.${clientId}`,
        },
        (payload) => {
          if (payload.new) {
            setSnapshotData(snapToData(payload.new));
            setSnapHistory((prev) => {
              const already = prev.some((h) => h.period === payload.new.period);
              return already
                ? prev
                : [
                    {
                      period: payload.new.period,
                      label: payload.new.period,
                      ref_date: payload.new.ref_date,
                    },
                    ...prev,
                  ];
            });
            setNotifications((prev) => [
              {
                id: `realtime_${Date.now()}`,
                type: "novo_snapshot",
                title: `Relatório ${payload.new.period} disponível`,
                message: "Novo snapshot importado pelo seu assessor.",
                read: false,
                created_at: new Date().toISOString(),
              },
              ...prev,
            ]);
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "snapshots",
          filter: `client_id=eq.${clientId}`,
        },
        (payload) => {
          if (payload.new) setSnapshotData(snapToData(payload.new));
        }
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED")
          logWarn("ClientLayout", `Inscrito em snapshots de ${clientId}`, { context: "realtime" });
      });
    return () => {
      channel.unsubscribe().then(() => supabase.removeChannel(channel));
    };
  }, [clientId]);

  const handleNotifClick = () => {
    setNotifOpen((o) => !o);
    const unread = notifications.filter((n) => !n.read);
    if (unread.length > 0) {
      markNotificationsRead(clientId).catch((e) =>
        logWarn("ClientLayout", e.message, { context: "markRead" })
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    }
  };

  // ── Objeto cliente: dados do Supabase (clients_meta + profiles) ───
  // FIX #310: blindar TODOS os campos que podem vir como objeto do Supabase
  const st = (v: unknown, fallback = "–") => safeText(v, "baseClient") || fallback;

  const baseClient = clientMetaRow
    ? {
        id: clientId,
        code: st(clientMetaRow.code, `LPA-${clientId}`),
        name: st((profile as any)?.full_name || clientNameProp, ""),
        shortName: st((profile as any)?.full_name || clientNameProp, ""),
        email: st((profile as any)?.email, ""),
        cpf: st((profile as any)?.cpf, "–"),
        phone: st((profile as any)?.phone, "–"),
        advisor: st(clientMetaRow.advisor, "–"),
        subAdvisor: st(clientMetaRow.sub_advisor, "–"),
        since: st(clientMetaRow.since, "–"),
        nextReview: st(clientMetaRow.next_review, "–"),
        lastUpdate: "–",
        patrimonio: { total: 0, xp: 0, btg: 0 },
        active: (profile as any)?.active ?? true,
        profile: st((profile as any)?.profile_risk, "–"),
        riskPts: clientMetaRow.risk_pts ?? null,
        riskMax: clientMetaRow.risk_max ?? 25,
        contaXP: st(clientMetaRow.conta_xp, "–"),
        contaBTG: st((clientMetaRow as any).conta_btg, "–"),
        contaAvenue: st((clientMetaRow as any).conta_avenue, "–"),
        contaInter: st((clientMetaRow as any).conta_inter, "–"),
      }
    : {
        id: clientId,
        code: `LPA-${clientId}`,
        name: st(clientNameProp, ""),
        shortName: st(clientNameProp, ""),
        email: st((profile as any)?.email, ""),
        cpf: st((profile as any)?.cpf, "–"),
        phone: st((profile as any)?.phone, "–"),
        advisor: "–",
        since: "–",
        lastUpdate: "–",
        patrimonio: { total: 0, xp: 0, btg: 0 },
        active: (profile as any)?.active ?? true,
        profile: st((profile as any)?.profile_risk, "–"),
        contaXP: "–",
        contaBTG: "–",
      };

  const latestSnap = snapshotData;

  // ── Conversão USD → BRL via PTAX ──────────────────────────────
  const patrimonioXP = (latestSnap as any)?.patrimonio_xp ?? baseClient.patrimonio?.xp ?? 0;
  const patrimonioBTG = (latestSnap as any)?.patrimonio_btg ?? baseClient.patrimonio?.btg ?? 0;
  const patrimonioInter =
    (latestSnap as any)?.patrimonio_inter ?? (baseClient.patrimonio as any)?.inter ?? 0;
  const patrimonioAvenueUSD =
    (latestSnap as any)?.patrimonio_avenue_usd ?? (baseClient.patrimonio as any)?.avenue_usd ?? 0;
  const avenueBRL = patrimonioAvenueUSD * ptaxRate;

  const client = {
    ...baseClient,
    patrimonio: {
      total: patrimonioXP + patrimonioBTG + patrimonioInter + avenueBRL,
      xp: patrimonioXP,
      btg: patrimonioBTG,
      inter: patrimonioInter,
      avenue_usd: patrimonioAvenueUSD,
      avenue_brl: avenueBRL, // conversão USD → BRL
      ptax: ptaxRate, // taxa PTAX utilizada
    },
  };

  const data = snapshotData || DATA_EMPTY;

  // ── FIX #310 (REAL FIX): useMemo MUST be called BEFORE any early return ──
  // Previously this was at line ~473, AFTER the `if (snapshotLoading)` and
  // `if (showSuitGate)` early returns. That caused a hooks-count mismatch:
  //   - Render 1 (loading=true): N hooks (useMemo skipped by early return)
  //   - Render 2 (loading=false): N+1 hooks (useMemo reached)
  // React Error #310: "Rendered more hooks than during the previous render"
  const hasSnapshot = snapshotData !== null;

  // ── Callback do DocumentosPage: re-fetch snapshot real do banco ──────
  // MUST be declared BEFORE clientContextValue useMemo that references it.
  // Using useCallback so the identity is stable across re-renders.
  const handleSnapshotCreated = useCallback(async () => {
    try {
      const [snap, hist] = await Promise.all([
        fetchLatestSnapshot(clientId),
        fetchSnapshotHistory(clientId),
      ]);
      if (snap) setSnapshotData(snapToData(snap));
      setSnapHistory(hist || []);
      setPage("dashboard");
    } catch (err: any) {
      logError("ClientLayout", err as Error, { context: "snapshot refetch" });
    }
  }, [clientId, setPage]);

  // ── Page list for command palette (MUST be before early returns) ────
  const pageList = useMemo(() => {
    return [...flattenNav(NAV), PERFIL_NAV];
  }, []);

  const clientContextValue = useMemo(
    () => ({
      T,
      dark,
      setDark,
      setPage,
      clientId,
      clientName: clientNameProp || userEmail,
      profile,
      snapshotData,
      snapshotLoading,
      snapHistory,
      data,
      hasSnapshot,
      client,
      suitStatus,
      notifications,
      clientSuitData,
      clientMetaRow,
      upsertClientMeta,
      setShowEditSuit,
      onSnapshotCreated: handleSnapshotCreated,
      privacyMode,
      setPrivacyMode,
    }),
    [
      T,
      dark,
      setDark,
      setPage,
      clientId,
      clientNameProp,
      userEmail,
      profile,
      snapshotData,
      snapshotLoading,
      snapHistory,
      data,
      hasSnapshot,
      client,
      suitStatus,
      notifications,
      clientSuitData,
      clientMetaRow,
      upsertClientMeta,
      setShowEditSuit,
      handleSnapshotCreated,
      privacyMode,
      setPrivacyMode,
    ]
  );

  const handlePeriodChange = async (period: string | null) => {
    if (!period || period === "__latest__") {
      setSelectedPeriod(null);
      if (_clientQueryData?.snap) setSnapshotData(snapToData(_clientQueryData.snap));
      return;
    }
    setSelectedPeriod(period);
    try {
      const snap = await fetchSnapshotByPeriod(clientId, period);
      if (snap) setSnapshotData(snapToData(snap));
    } catch (e: any) {
      logWarn("ClientLayout", e?.message || String(e), { context: "fetch period snapshot" });
    }
  };

  // ── Skeleton loading ──────────────────────────────────────────────
  if (snapshotLoading) {
    return (
      <div
        style={{
          display: "flex",
          minHeight: "100vh",
          background: T.bg,
          fontFamily: "'Inter','Segoe UI',sans-serif",
        }}
      >
        <div
          style={{
            width: 240,
            background: `linear-gradient(180deg,${T.accent} 0%,${T.accentHover} 100%)`,
            padding: 20,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 32,
              paddingBottom: 20,
              borderBottom: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                background: "#C9A227",
                flexShrink: 0,
              }}
            />
            <SkeletonBox h={16} w={100} dark={true} />
          </div>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                padding: "10px 8px",
                marginBottom: 4,
              }}
            >
              <SkeletonBox h={16} w={16} br={4} dark={true} />
              <SkeletonBox h={12} w={`${55 + i * 5}%`} dark={true} />
            </div>
          ))}
        </div>
        <div style={{ flex: 1, padding: 26 }}>
          <div style={{ marginBottom: 24 }}>
            <SkeletonBox h={22} w={200} mb={8} />
            <SkeletonBox h={11} w={300} />
          </div>
          <SkeletonMetricRow />
          <SkeletonMetricRow />
          <SkeletonCard lines={4} T={T} />
        </div>
      </div>
    );
  }

  // ── Suitability gate ──────────────────────────────────────────────
  if (showSuitGate) {
    return (
      <ClientSuitabilityGate
        clientId={clientId}
        profile={profile as any}
        T={T}
        onComplete={(savedData: any) => {
          if (savedData) {
            setSuitStatus(savedData.status || "parcial");
            setClientSuitData(savedData);
          }
          setShowSuitGate(false);
        }}
      />
    );
  }

  // ── Páginas renderizadas (vêm do App.jsx via prop) ────────────────
  // Após migração do ClientContext, apenas `hasSnapshot` é necessário
  // para decidir entre página real e EmptyState.
  const PAGES = pages({ hasSnapshot });

  // Skip-to-main link styles (WCAG AAA)
  const skipLinkStyle = {
    position: "fixed" as const,
    top: "-100px",
    left: "8px",
    background: "#0B2447",
    color: "white",
    padding: "8px 16px",
    borderRadius: "0 0 8px 8px",
    zIndex: 9999,
    fontSize: "14px",
    fontWeight: 700 as const,
    textDecoration: "none",
    transition: "top 0.15s ease-in-out",
  };

  return (
    <ClientProvider value={clientContextValue as unknown as ClientContextValue}>
      <a
        href="#main-content"
        style={skipLinkStyle}
        onFocus={(e: React.FocusEvent<HTMLAnchorElement>) => {
          (e.target as HTMLAnchorElement).style.top = "0";
        }}
        onBlur={(e: React.FocusEvent<HTMLAnchorElement>) => {
          (e.target as HTMLAnchorElement).style.top = "-100px";
        }}
      >
        Pular para conteúdo principal
      </a>
      <div
        style={{
          display: "flex",
          minHeight: "100vh",
          background: T.bg,
          fontFamily: "'Inter','Segoe UI','Helvetica Neue',sans-serif",
        }}
      >
        <Sidebar
          active={page}
          setActive={setPage}
          onLogout={onLogout}
          col={col}
          setCol={setCol}
          dark={dark}
          setDark={setDark}
          T={T}
          client={client}
          notifications={notifications}
          onNotifClick={handleNotifClick}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          privacyMode={privacyMode}
          setPrivacyMode={setPrivacyMode}
        />
        <main
          id="main-content"
          ref={(el: HTMLElement | null) => {
            if (el) {
              (mainRef as any).current = el;
              (swipeRef as any).current = el;
            }
          }}
          style={{
            flex: 1,
            overflowY: "auto",
            padding: isMobile ? "16px 14px 80px" : 26,
            minWidth: 0,
            marginLeft: isMobile ? 0 : undefined,
          }}
        >
          <LayoutHeader
            isMobile={isMobile}
            clientNameProp={clientNameProp}
            _mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
          />

          <PeriodSelector
            snapHistory={snapHistory}
            selectedPeriod={selectedPeriod}
            onPeriodChange={handlePeriodChange}
          />

          {/* Banner de onboarding incompleto */}
          {(suitStatus === "pendente" || suitStatus === "parcial") && !showSuitGate && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                background: "linear-gradient(135deg, #C9A22718 0%, #C9A22708 100%)",
                border: `1px solid ${T.accent || "#C9A227"}44`,
                borderRadius: 12,
                padding: "14px 20px",
                marginBottom: 20,
              }}
            >
              <span style={{ fontSize: 22 }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: T.text, marginBottom: 2 }}>
                  {suitStatus === "pendente" ? "Onboarding Pendente" : "Onboarding Incompleto"}
                </div>
                <div style={{ fontSize: 13, color: T.sub || T.text + "99", lineHeight: 1.4 }}>
                  {suitStatus === "pendente"
                    ? "Complete seu perfil de investidor para ter acesso completo ao portal."
                    : "Seu perfil de investidor está parcialmente preenchido. Complete-o para liberar todos os recursos."}
                </div>
              </div>
              <button
                onClick={() => setShowSuitGate(true)}
                style={{
                  background: T.accent || "#C9A227",
                  color: "#0B2447",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 18px",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "opacity 0.15s",
                }}
                onMouseOver={(e: React.MouseEvent<HTMLButtonElement>) =>
                  (e.currentTarget.style.opacity = "0.85")
                }
                onMouseOut={(e: React.MouseEvent<HTMLButtonElement>) =>
                  (e.currentTarget.style.opacity = "1")
                }
              >
                Completar Onboarding
              </button>
            </div>
          )}

          {/* Página ativa (Suspense para lazy loading + transição) */}
          <Suspense
            fallback={<SkeletonPulse width={80} height={14} style={{ display: "inline-block" }} />}
          >
            <PageErrorBoundary T={T} pageName={page}>
              <PageTransition pageKey={page}>{PAGES[page] || PAGES.dashboard}</PageTransition>
            </PageErrorBoundary>
          </Suspense>

          {/* Wizard de edição de perfil */}
          {showEditSuit && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 500,
                background: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: 720,
                  maxHeight: "90vh",
                  overflowY: "auto",
                  borderRadius: 18,
                }}
              >
                <ClientSuitabilityGate
                  clientId={clientId}
                  profile={profile as any}
                  T={T}
                  onComplete={async (savedData: any) => {
                    if (savedData) setClientSuitData(savedData);
                    setShowEditSuit(false);
                  }}
                />
                <button
                  onClick={() => setShowEditSuit(false)}
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: 8,
                    padding: "12px 0",
                    background: "rgba(255,255,255,0.15)",
                    color: T.accentText,
                    border: "none",
                    borderRadius: 12,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  ✕ Fechar sem salvar
                </button>
              </div>
            </div>
          )}

          <NotificationsPanel
            notifOpen={notifOpen}
            notifications={notifications}
            onClose={() => setNotifOpen(false)}
            col={col}
          />

          {/* Auto-triggered Onboarding Wizard */}
          {showAutoWizard && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 10000,
                background: "rgba(0,0,0,0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(4px)",
              }}
            >
              <div
                style={{
                  width: "90%",
                  maxWidth: 800,
                  maxHeight: "90vh",
                  overflow: "auto",
                  borderRadius: "16px",
                  background: T.card,
                  boxShadow: "0 25px 50px rgba(0,0,0,0.4)",
                }}
              >
                <OnboardingWizard
                  clientId={clientId}
                  clientName={client.name as string}
                  T={T}
                  onClose={() => setShowAutoWizard(false)}
                  onSave={async (cId: string, formData: any) => {
                    setShowAutoWizard(false);
                    setSuitStatus(formData.status || "parcial");
                    // Mark onboarding as done so tour shows next
                    localStorage.setItem("lpa_onboarding_done", "1");
                    setShowOnboarding(true);
                  }}
                />
              </div>
            </div>
          )}

          {/* Onboarding Tour */}
          {showOnboarding && <OnboardingTour T={T} onComplete={() => setShowOnboarding(false)} />}

          {/* Footer */}
          <div
            className="no-print"
            style={{
              marginTop: 36,
              paddingTop: 16,
              borderTop: `1px solid ${T.border}`,
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <span style={{ fontSize: 10, color: T.muted }}>
              © 2026 LPA Wealth Investment Advisory · Todos os direitos reservados
            </span>
            <span style={{ fontSize: 10, color: T.muted }}>
              As informações são de caráter informativo e não constituem oferta de valores
              mobiliários.
            </span>
          </div>
        </main>
      </div>

      {/* Command Palette */}
      {showCommandPalette && (
        <CommandPalette
          T={T}
          pages={pageList as any}
          onNavigate={setPage}
          onClose={() => setShowCommandPalette(false)}
        />
      )}

      {/* Shortcuts Help */}
      {showShortcutsHelp && <ShortcutsHelp T={T} onClose={() => setShowShortcutsHelp(false)} />}

      {/* Push Notification opt-in (D4) */}
      <PushNotificationBanner />

      {/* Mobile Tab Bar (Fase 5 — PWA) */}
      {isMobile && <MobileTabBar active={page} setActive={setPage} T={T} />}

      {/* Session timeout warning */}
      {showSessionWarning && (
        <SessionTimeoutWarning
          showWarning={showSessionWarning}
          remainingSeconds={remainingSeconds}
          onContinue={dismissWarning}
          onLogout={handleSessionExpired}
          T={T}
        />
      )}
    </ClientProvider>
  );
}
